import { Currency } from '../enum/currency'
import { Factory } from '../interface/factory'
import { Identifiable } from '../interface/identifiable'
import { Modifiable } from '../interface/modifiable'
import { Price } from './price-cap'
import { Rate } from './rate'
import { Duration } from './settings'
import {
  applyEntitySchema,
  objectSchema,
  parseEntity,
  sourceJson,
  wireValue,
} from '../../_utils/fcdc-v2'

const payloadSchema = objectSchema({})

function rememberPayload(entity: object, json: unknown): void {
  applyEntitySchema(entity, payloadSchema, wireValue(sourceJson(json ?? {})), {})
}

// #region Summary
export enum SummaryType {
  Confirmation = 'Confirmation',
  ExpiryExtension = 'ExpiryExtension',
  ExtraUnlocks = 'ExtraUnlocks',
  Addons = 'Addons',
  Termination = 'Termination',
  PayOnDeliveryTermination = 'PayOnDeliveryTermination',
  Unknown = 'Unknown',
}

export enum ServiceProvider {
  Adyen = 'Adyen',
}

export enum MethodType {
  Card = 'Card',
}

export abstract class Summary {
  type: SummaryType

  constructor(type: SummaryType) {
    this.type = type
  }

  static getFactory(): Factory<Summary> {
    return new (class implements Factory<Summary> {
      make(json: any): Summary {
        switch (json.type) {
          case SummaryType.Confirmation:
            return new SummaryConfirmation(json)
          case SummaryType.ExpiryExtension:
            return new SummaryExpiryExtension(json)
          case SummaryType.ExtraUnlocks:
            return new ExtraUnlocks(json)
          case SummaryType.Addons:
            return new Addons(json)
          case SummaryType.Termination:
            return new Termination(json)
          case SummaryType.PayOnDeliveryTermination:
            return new PayOnDeliveryTermination(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as Summary
        }
      }

      getTableName(): string {
        throw new Error('Summary does not have its own table')
      }
    })()
  }
}

export class SummaryConfirmation extends Summary {
  startPrice?: Price
  priceFrames: PriceFrame[]
  unlocks?: PurchasedUnlocks
  beneficiaries?: PurchasedBeneficiaries
  expiryReminderSms?: Price
  serviceFee?: Price

  constructor(json: any) {
    super(SummaryType.Confirmation)
    this.startPrice = json.start_price
    this.priceFrames = json.priceFrames?.length
      ? json.priceFrames.map((frame: any) =>
          PriceFrame.getFactory().make(frame)
        )
      : []
    this.unlocks = json.unlocks
    this.beneficiaries = json.beneficiaries
    this.expiryReminderSms = json.expiryReminderSms
    this.serviceFee = json.serviceFee
    rememberPayload(this, json)
  }
}

export class SummaryExpiryExtension extends Summary {
  priceFrames: PriceFrame[]

  constructor(json: any) {
    super(SummaryType.ExpiryExtension)
    this.priceFrames = json.priceFrames?.length
      ? json.priceFrames.map((frame: any) =>
          PriceFrame.getFactory().make(frame)
        )
      : []
    rememberPayload(this, json)
  }
}

export class ExtraUnlocks extends Summary {
  unlocks: PurchasedUnlocks

  constructor(json: any) {
    super(SummaryType.ExtraUnlocks)
    this.unlocks = json.unlocks
    rememberPayload(this, json)
  }
}

export class Addons extends Summary {
  unlocks?: PurchasedUnlocks
  expiryReminderSms?: Price

  constructor(json: any) {
    super(SummaryType.Addons)
    this.unlocks = json.unlocks
    this.expiryReminderSms = json.expiryReminderSms
    rememberPayload(this, json)
  }
}

export class Termination extends Summary {
  priceFrames: PriceFrame[]

  constructor(json: any) {
    super(SummaryType.Termination)
    this.priceFrames = json.priceFrames?.length
      ? json.priceFrames.map((frame: any) =>
          PriceFrame.getFactory().make(frame)
        )
      : []
    rememberPayload(this, json)
  }
}

export class PayOnDeliveryTermination extends Summary {
  amount: string

  constructor(json: any) {
    super(SummaryType.PayOnDeliveryTermination)
    this.amount = json.amount
    rememberPayload(this, json)
  }
}

// #region Payment
export class Payment implements Identifiable, Modifiable {
  id: string
  userId: string
  balance: string
  currency: Currency
  deposition?: Deposition
  vat?: number
  dcc?: DynamicCurrencyConversion
  summary?: Summary
  history: HistoryEvent[]
  created: Date
  modified: Date
  totalHistoryAmount?: number

  constructor(json: any) {
    this.id = json.id
    this.userId = json.userId
    this.balance = json.balance
    this.currency = json.currency
    this.deposition = json.deposition
      ? Deposition.getFactory().make(json.deposition)
      : undefined
    this.dcc = json.dcc
      ? DynamicCurrencyConversion.getFactory().make(json.dcc)
      : undefined
    this.vat = json.vat
    this.summary = json.summary
      ? Summary.getFactory().make(json.summary)
      : undefined

    this.created = new Date(json.created)
    this.modified = new Date(json.modified)

    this.history =
      json.history && json.history.length
        ? json.history.map((h: any) => HistoryEvent.getFactory().make(h))
        : []

    let total = 0
    for (const event of this.history) {
      switch (event.type) {
        case HistoryEventType.CardTransactionSucceeded: {
          const success = event as CardTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }
        case HistoryEventType.SwishTransactionSucceeded: {
          const success = event as SwishTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }
        case HistoryEventType.ApplePayTransactionSucceeded: {
          const success = event as ApplePayTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }
        case HistoryEventType.VippsTransactionSucceeded: {
          const success = event as VippsTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }
        case HistoryEventType.PaymentTerminalTransactionSucceeded: {
          const success = event as PaymentTerminalTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }
        case HistoryEventType.GooglePayTransactionSucceeded: {
          const success = event as GooglePayTransactionSucceeded
          total += parseFloat(success.amount)
          break
        }

        case HistoryEventType.Free: {
          const free = event as FreeEvent
          total += parseFloat(free.amount)
          break
        }

        case HistoryEventType.RefundSucceeded: {
          const refund = event as RefundSucceeded
          total -= parseFloat(refund.amount)
          break
        }

        default:
          break
      }
    }
    this.totalHistoryAmount = total
    rememberPayload(this, json)
  }

  /** true if the customer was charged (has at least one paid history event). Free-only payments are not refundable. */
  get isRefundable(): boolean {
    if (!this.history?.length) return false
    const paidTypes = [
      HistoryEventType.CardTransactionSucceeded,
      HistoryEventType.SwishTransactionSucceeded,
      HistoryEventType.ApplePayTransactionSucceeded,
      HistoryEventType.VippsTransactionSucceeded,
      HistoryEventType.PaymentTerminalTransactionSucceeded,
      HistoryEventType.GooglePayTransactionSucceeded,
    ]
    return this.history.some(e => paidTypes.includes(e.type))
  }

  public static getFactory(): Factory<Payment> {
    return new (class implements Factory<Payment> {
      make(json: any): Payment {
        return new Payment(json)
      }

      getTableName(): string {
        return 'payments'
      }
    })()
  }
}
// #endregion

// #region Deposition
export enum DepositionType {
  Paid = 'Paid',
  Refunded = 'Refunded',
}

export abstract class Deposition {
  type: DepositionType
  amount: string

  constructor(json: any, type: DepositionType) {
    this.type = type
    this.amount = json.amount
    rememberPayload(this, json)
  }

  static getFactory(): Factory<Deposition> {
    return new (class implements Factory<Deposition> {
      make(json: any): Deposition {
        const depositionType = json.type || json.status
        switch (depositionType) {
          case DepositionType.Paid:
            return new DepositionPaid(json)
          case DepositionType.Refunded:
            return new DepositionRefunded(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as Deposition
        }
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on a Deposition. Depositions are not stored in IndexDB.'
        )
      }
    })()
  }
}

export class DepositionPaid extends Deposition {
  constructor(json: any) {
    super(json, DepositionType.Paid)
  }
}

export class DepositionRefunded extends Deposition {
  constructor(json: any) {
    super(json, DepositionType.Refunded)
  }
}

// #region PriceFrame
export enum PriceFrameType {
  Capped = 'Capped',
  Rate = 'Rate',
  Slot = 'Slot',
}

export abstract class PriceFrame {
  type: PriceFrameType
  start: Date
  end: Date

  constructor(json: any, type: PriceFrameType) {
    this.type = type
    this.start = new Date(json.start)
    this.end = new Date(json.end)
    rememberPayload(this, json)
  }

  static getFactory(): Factory<PriceFrame> {
    return new (class implements Factory<PriceFrame> {
      make(json: any): PriceFrame {
        switch (json.type) {
          case PriceFrameType.Capped:
            return new CappedPriceFrame(json)
          case PriceFrameType.Rate:
            return new RatePriceFrame(json)
          case PriceFrameType.Slot:
            return new SlotPriceFrame(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as PriceFrame
        }
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on a PriceFrame. PriceFrames are not stored in IndexDB.'
        )
      }
    })()
  }
}

export class CappedPriceFrame extends PriceFrame {
  price: Price
  blockLength: number

  constructor(json: any) {
    super(json, PriceFrameType.Capped)
    this.price = json.price
    this.blockLength = json.blockLength
  }
}

export class RatePriceFrame extends PriceFrame {
  rate: Rate

  constructor(json: any) {
    super(json, PriceFrameType.Rate)
    this.rate = json.rate
  }
}

export class SlotPriceFrame extends PriceFrame {
  price: Price
  slotLength: Duration

  constructor(json: any) {
    super(json, PriceFrameType.Slot)
    this.price = json.price
    this.slotLength = json.slotLength
  }
}
// #endregion

// #region PurchasedUnlocks
export enum PurchasedUnlocksType {
  Limited = 'Limited',
  Unlimited = 'Unlimited',
}

export abstract class PurchasedUnlocks {
  type: PurchasedUnlocksType
  price: Price

  constructor(json: any, type: PurchasedUnlocksType) {
    this.type = type
    this.price = json.price
    rememberPayload(this, json)
  }

  static getFactory(): Factory<PurchasedUnlocks> {
    return new (class implements Factory<PurchasedUnlocks> {
      make(json: any): PurchasedUnlocks {
        switch (json.type) {
          case PurchasedUnlocksType.Limited:
            return new LimitedPurchasedUnlocks(json)
          case PurchasedUnlocksType.Unlimited:
            return new UnlimitedPurchasedUnlocks(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as PurchasedUnlocks
        }
      }

      getTableName(): string {
        throw new Error('PurchasedUnlocks does not have its own table')
      }
    })()
  }
}

export class LimitedPurchasedUnlocks extends PurchasedUnlocks {
  number: number

  constructor(json: any) {
    super(json, PurchasedUnlocksType.Limited)
    this.number = json.number
  }
}

export class UnlimitedPurchasedUnlocks extends PurchasedUnlocks {
  constructor(json: any) {
    super(json, PurchasedUnlocksType.Unlimited)
  }
}

export interface PurchasedBeneficiaries {
  type: 'Limited' | 'Unlimited'
  number?: number
  price?: Price
  basePrice?: Price
}

// #endregion

// #region HistoryEvent
export enum HistoryEventType {
  CardTransactionSucceeded = 'CardTransactionSucceeded',
  RefundSucceeded = 'RefundSucceeded',
  SwishTransactionSucceeded = 'SwishTransactionSucceeded',
  ApplePayTransactionSucceeded = 'ApplePayTransactionSucceeded',
  VippsTransactionSucceeded = 'VippsTransactionSucceeded',
  Free = 'Free',
  PaymentTerminalTransactionSucceeded = 'PaymentTerminalTransactionSucceeded',
  GooglePayTransactionSucceeded = 'GooglePayTransactionSucceeded',
  SwishTransactionFailed = 'SwishTransactionFailed',
  VippsTransactionFailed = 'VippsTransactionFailed',
  Unknown = 'Unknown',
}

export abstract class HistoryEvent {
  type: HistoryEventType
  created: Date

  constructor(json: any, type: HistoryEventType) {
    this.type = type
    this.created = json.created ? new Date(json.created) : new Date()
    rememberPayload(this, json)
  }

  static getFactory(): Factory<HistoryEvent> {
    return new (class implements Factory<HistoryEvent> {
      make(json: any): HistoryEvent {
        switch (json.type) {
          case HistoryEventType.CardTransactionSucceeded:
            return new CardTransactionSucceeded(json)
          case HistoryEventType.RefundSucceeded:
            return new RefundSucceeded(json)
          case HistoryEventType.SwishTransactionSucceeded:
            return new SwishTransactionSucceeded(json)
          case HistoryEventType.ApplePayTransactionSucceeded:
            return new ApplePayTransactionSucceeded(json)
          case HistoryEventType.VippsTransactionSucceeded:
            return new VippsTransactionSucceeded(json)
          case HistoryEventType.Free:
            return new FreeEvent(json)
          case HistoryEventType.PaymentTerminalTransactionSucceeded:
            return new PaymentTerminalTransactionSucceeded(json)
          case HistoryEventType.GooglePayTransactionSucceeded:
            return new GooglePayTransactionSucceeded(json)
          case HistoryEventType.SwishTransactionFailed:
            return new SwishTransactionFailed(json)
          case HistoryEventType.VippsTransactionFailed:
            return new VippsTransactionFailed(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as HistoryEvent
        }
      }
      getTableName(): string {
        throw new Error('HistoryEvents do not have their own table')
      }
    })()
  }
}

export class CardTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference
  paymentMethodId: string

  constructor(json: any) {
    super(json, HistoryEventType.CardTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
    this.paymentMethodId = json.paymentMethodId
  }
}

export class RefundSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference

  constructor(json: any) {
    super(json, HistoryEventType.RefundSucceeded)
    this.amount = json.amount
    this.reference = json.reference
  }
}

export class SwishTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference

  constructor(json: any) {
    super(json, HistoryEventType.SwishTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
  }
}

export class ApplePayTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference

  constructor(json: any) {
    super(json, HistoryEventType.ApplePayTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
  }
}
export class VippsTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference

  constructor(json: any) {
    super(json, HistoryEventType.VippsTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
  }
}

export class FreeEvent extends HistoryEvent {
  amount: string

  constructor(json: any) {
    super(json, HistoryEventType.Free)
    this.amount = json.amount
  }
}

export class TerminalPaymentBrand {
  name: string
  value?: string

  constructor(json: any) {
    this.name = json.name
    this.value = json.value
    rememberPayload(this, json)
  }

  static getFactory(): Factory<TerminalPaymentBrand> {
    return new (class implements Factory<TerminalPaymentBrand> {
      make(json: any): TerminalPaymentBrand {
        return new TerminalPaymentBrand(json)
      }

      getTableName(): string {
        throw new Error('TerminalPaymentBrand does not have its own table')
      }
    })()
  }
}

export class TerminalEntryMode {
  name: string
  value?: string

  constructor(json: any) {
    this.name = json.name
    this.value = json.value
    rememberPayload(this, json)
  }

  static getFactory(): Factory<TerminalEntryMode> {
    return new (class implements Factory<TerminalEntryMode> {
      make(json: any): TerminalEntryMode {
        return new TerminalEntryMode(json)
      }

      getTableName(): string {
        throw new Error('TerminalEntryMode does not have its own table')
      }
    })()
  }
}

export class PaymentTerminalTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference
  paymentMethodId?: string
  entryMode?: TerminalEntryMode
  paymentBrand?: TerminalPaymentBrand

  constructor(json: any) {
    super(json, HistoryEventType.PaymentTerminalTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
    this.paymentMethodId = json.paymentMethodId
    this.entryMode = json.entryMode
      ? TerminalEntryMode.getFactory().make(json.entryMode)
      : undefined
    this.paymentBrand = json.paymentBrand
      ? TerminalPaymentBrand.getFactory().make(json.paymentBrand)
      : undefined
  }
}

export class GooglePayTransactionSucceeded extends HistoryEvent {
  amount: string
  reference: ServiceProviderReference

  constructor(json: any) {
    super(json, HistoryEventType.GooglePayTransactionSucceeded)
    this.amount = json.amount
    this.reference = ServiceProviderReference.getFactory().make(json.reference)
  }
}

export class SwishTransactionFailed extends HistoryEvent {
  reason?: any
  nonce?: string

  constructor(json: any) {
    super(json, HistoryEventType.SwishTransactionFailed)
    this.reason = json.reason
    this.nonce = json.nonce
  }
}

export class VippsTransactionFailed extends HistoryEvent {
  reason?: any
  nonce?: string

  constructor(json: any) {
    super(json, HistoryEventType.VippsTransactionFailed)
    this.reason = json.reason
    this.nonce = json.nonce
  }
}

// #region ServiceProviderReference
export enum ServiceProviders {
  Adyen = 'Adyen',
}

export abstract class ServiceProviderReference {
  type: string

  constructor(json?: any) {
    if (json) {
      this.type = json.type || json.name
    } else {
      this.type = ''
    }
    rememberPayload(this, json)
  }

  static getFactory(): Factory<ServiceProviderReference> {
    return new (class implements Factory<ServiceProviderReference> {
      make(json: any): ServiceProviderReference {
        const refType = json.type || json.name
        switch (refType) {
          case ServiceProviders.Adyen:
            return new Adyen(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as ServiceProviderReference
        }
      }

      getTableName(): string {
        throw new Error('ServiceProviderReference does not have its own table')
      }
    })()
  }
}

export class Adyen extends ServiceProviderReference {
  reference: string
  merchantAccount: string

  constructor(json: any) {
    super(json)
    this.reference = json.reference
    this.merchantAccount = json.merchantAccount
  }
}

// #region DynamicCurrencyConversion
export enum DynamicCurrencyConversionType {
  Accepted = 'Accepted',
  Rejected = 'Rejected',
}

export abstract class DynamicCurrencyConversion {
  type: DynamicCurrencyConversionType
  localAmount: number
  localCurrency: string
  billingAmount: number
  billingCurrency: string
  markup: number
  rate: number
  merchantAccount: string

  constructor(json: any) {
    this.type = json.type
    this.localAmount = json.localAmount
    this.localCurrency = json.localCurrency
    this.billingAmount = json.billingAmount
    this.billingCurrency = json.billingCurrency
    this.markup = json.markup
    this.rate = json.rate
    this.merchantAccount = json.merchantAccount
    rememberPayload(this, json)
  }

  static getFactory(): Factory<DynamicCurrencyConversion> {
    return new (class implements Factory<DynamicCurrencyConversion> {
      make(json: any): DynamicCurrencyConversion {
        switch (json.type) {
          case DynamicCurrencyConversionType.Accepted:
            return new AcceptedDynamicCurrencyConversion(json)
          case DynamicCurrencyConversionType.Rejected:
            return new RejectedDynamicCurrencyConversion(json)
          default:
            return parseEntity(payloadSchema, json ?? {}) as unknown as DynamicCurrencyConversion
        }
      }
      getTableName(): string {
        throw new Error('DynamicCurrencyConversion does not have its own table')
      }
    })()
  }
}

export class AcceptedDynamicCurrencyConversion extends DynamicCurrencyConversion {
  exchangeRateSource: string
  constructor(json: any) {
    super(json)
    this.exchangeRateSource = json.exchangeRateSource
  }
}

export class RejectedDynamicCurrencyConversion extends DynamicCurrencyConversion {
  constructor(json: any) {
    super(json)
  }
}

// #endregion
